import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '@mui/material'
import { formatUSPhoneNumber } from '../utilities/utils'

const DEMO_CONSENT_TEXT =
    "By submitting this form, I consent to be contacted by Final Debt Relief and its partners " +
    "via telephone, text message, and email regarding debt relief services, even if my number " +
    "is on the Do Not Call Registry. I understand that my consent is not a condition of purchase " +
    "and that I may revoke this consent at any time. I have read and agree to the Privacy Policy " +
    "and Terms of Service. Message and data rates may apply."

// ─── small reusable field row ──────────────────────────────────────────────────
function Field({ label, value }) {
    return (
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e4e6eb' }}>
            <div style={{ fontSize: '12px', color: '#65676b', marginBottom: '4px', fontWeight: 500 }}>
                {label}
            </div>
            <div style={{ fontSize: '15px', color: '#1c1e21', wordBreak: 'break-all' }}>
                {value || <span style={{ color: '#bcc0c4', fontStyle: 'italic' }}>—</span>}
            </div>
        </div>
    )
}

// ─── section header ────────────────────────────────────────────────────────────
function SectionHeader({ children }) {
    return (
        <div style={{
            padding: '12px 20px 10px',
            fontSize: '13px',
            fontWeight: 700,
            color: '#65676b',
            textTransform: 'uppercase',
            letterSpacing: '.6px',
            background: '#f0f2f5',
            borderBottom: '1px solid #e4e6eb'
        }}>
            {children}
        </div>
    )
}

// ─── main page ─────────────────────────────────────────────────────────────────
const FbConsentPage = ({ title }) => {
    document.querySelector('title').innerHTML = title

    const { contact_id } = useParams()

    const { data: user, isLoading, isError, error } = useQuery({
        queryFn: async () => {
            const res = await fetch(`/api/fb-lead/${contact_id}`, { credentials: 'include' })
            if (!res.ok) throw new Error(res.statusText)
            const json = await res.json()
            if (!json.success) throw new Error(json.msg || 'Failed to load lead')
            return json.data
        },
        queryKey: ['fb-lead', contact_id],
        staleTime: 60 * 60 * 1000,
        retry: false
    })

    console.log(error)

    return (
        <div style={{ minHeight: '100dvh', background: '#f0f2f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

            {/* ── Facebook-style header ── */}
            <div style={{ background: '#1877F2', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px', position: 'sticky', top: 0, zIndex: 10 }}>
                <div>
                    <div style={{ color: 'white', fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
                        Facebook Lead Form
                    </div>
                    <div style={{ color: 'rgba(255,255,255,.75)', fontSize: '12px' }}>
                        Read-only consent record
                    </div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    <span style={{
                        background: '#23a55a', color: 'white', fontSize: '11px', fontWeight: 700,
                        padding: '3px 10px', borderRadius: '100px', letterSpacing: '.3px'
                    }}>
                        ✓ SUBMITTED
                    </span>
                </div>
            </div>

            {/* ── body ── */}
            <div style={{ maxWidth: 560, margin: '24px auto', padding: '0 12px 40px' }}>

                {isLoading && (
                    <div style={{ borderRadius: 12, overflow: 'hidden', background: 'white' }}>
                        {[...Array(8)].map((_, i) => (
                            <div key={i} style={{ padding: '14px 20px', borderBottom: '1px solid #e4e6eb' }}>
                                <Skeleton variant="text" width="40%" height={14} />
                                <Skeleton variant="text" width="70%" height={20} style={{ marginTop: 4 }} />
                            </div>
                        ))}
                    </div>
                )}

                {isError && !user && (
                    <div style={{
                        borderRadius: 12, background: 'white', padding: '32px 24px', textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,.12)'
                    }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
                        <p style={{ fontWeight: 700, fontSize: '16px', color: '#1c1e21', margin: 0 }}>
                            Unable to load lead
                        </p>
                        <p style={{ color: '#65676b', fontSize: '14px', marginTop: '6px' }}>
                            {error?.message || 'Something went wrong.'}
                        </p>
                    </div>
                )}

                {user && !isError && (
                    <>
                        {/* ── card: contact info ── */}
                        <div style={{ borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.12)', marginBottom: 16 }}>
                            <SectionHeader>Contact Information</SectionHeader>
                            <Field label="First Name" value={user.first_name} />
                            <Field label="Last Name" value={user.last_name} />
                            <Field label="Email Address" value={user.email} />
                            <Field label="Phone Number" value={formatUSPhoneNumber(user.phone)} />
                        </div>

                        {/* ── card: form metadata ── */}
                        <div style={{ borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.12)', marginBottom: 16 }}>
                            <SectionHeader>Form Details</SectionHeader>
                            <Field label="Form ID" value={user.form_id} />
                            <Field label="Form Name" value={user.form_name} />
                            <Field label="Source" value={user.source} />
                            <Field label="Session Source" value={user.session_source} />
                            <Field label="Medium ID" value={user.medium_id} />
                        </div>

                        {/* ── card: consent ── */}
                        <div style={{ borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.12)' }}>
                            <SectionHeader>Consent Agreement</SectionHeader>
                            <div style={{ padding: '16px 20px' }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
                                    <div style={{
                                        width: 20, height: 20, borderRadius: 4, background: '#1877F2',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0, marginTop: 1
                                    }}>
                                        <span style={{ color: 'white', fontSize: '13px', fontWeight: 900 }}>✓</span>
                                    </div>
                                    <span style={{ fontSize: '14px', color: '#1c1e21', fontWeight: 600 }}>
                                        User agreed to the following consent
                                    </span>
                                </div>
                                <div style={{
                                    background: '#f0f2f5', borderRadius: 8, padding: '14px 16px',
                                    fontSize: '13px', color: '#444950', lineHeight: 1.6,
                                    borderLeft: '3px solid #1877F2'
                                }}>
                                    {DEMO_CONSENT_TEXT}
                                </div>
                            </div>
                        </div>

                        {/* ── footer note ── */}
                        <p style={{ textAlign: 'center', fontSize: '12px', color: '#bcc0c4', marginTop: 20 }}>
                            This is a read-only record of the Facebook lead form submission.
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}

export default FbConsentPage
